import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { api } from "../../api";
import { useMyShoes } from "../../shoesApi";
import { isCalledOffStatus, isDoneStatus } from "../../workoutStatus";
import { computeCarbPlan } from "../../utils/carbTiming";
import { CARB_FUELING_MIN_DURATION_MIN, PRECIP_WARN_PROB } from "../../lib/scienceThresholds";

/**
 * W-IA-2e/2f (IA_CANON_v2 раздел 2.4) — протокол исполнения под SessionHero.
 *
 * Фаза дня:
 *  - тренировка запланирована → fueling-план (computeCarbPlan, та же
 *    математика что /carb-timing) + обувь из ротации (run/trail);
 *  - тренировка выполнена → дебриф-строка (факт vs план), детали — в
 *    разборе тренировки.
 *
 * ТД-125 закрыт: окно погоды (weather/training-window) + исполнение
 * (target_zone/HR-cap из IF+LTHR, execution_targets service).
 */

type TrainingWindow = {
  start_hour: number;
  end_hour: number;
  avg_temp_c: number;
  max_precip_prob: number;
  score: number;
};

function useTrainingWindow(enabled: boolean) {
  return useQuery<TrainingWindow | null>({
    queryKey: ["athlete-weather-training-window"],
    queryFn: async () =>
      (await api.get<TrainingWindow | null>("/athlete/weather/training-window")).data,
    staleTime: 30 * 60 * 1000,
    retry: false,
    enabled,
  });
}

export interface ProtocolWorkout {
  id: number;
  target_zone?: string | null;
  target_hr_max?: number | null;
  discipline: string | null;
  status: string | null;
  planned_duration_minutes: number | null;
  planned_distance_km: number | null;
  actual_duration_minutes: number | null;
  actual_distance_km: number | null;
  rpe: number | null;
}

const RUN_LIKE = new Set(["run", "trail"]);

export function TodayProtocolRow({
  workout,
  bodyWeightKg,
  embedded = false,
}: {
  workout: ProtocolWorkout | null;
  bodyWeightKg: number | null;
  /** true → рендер внутри hero-карты: без собственного indigo-card. */
  embedded?: boolean;
}) {
  const { t } = useTranslation("dashboard");
  const isRunLike = RUN_LIKE.has(workout?.discipline ?? "");
  const { data: shoes } = useMyShoes();
  // Отменённая/перенесённая сессия — НЕ «запланированная»: протокол
  // питания для неё советовать нечего.
  const planned =
    !!workout && !isDoneStatus(workout.status) && !isCalledOffStatus(workout.status);
  const { data: win } = useTrainingWindow(planned);

  if (!workout) return null;
  if (isCalledOffStatus(workout.status)) return null;

  // ── Фаза «после тренировки» — дебриф ──
  if (isDoneStatus(workout.status)) {
    const dist = workout.actual_distance_km;
    const dur = workout.actual_duration_minutes;
    return (
      <Link
        to={`/workout/${workout.id}`}
        data-testid="today-protocol-debrief"
        className={
          embedded
            ? "mt-3 rounded-md bg-bg-raised px-3 py-2.5 flex items-center justify-between gap-3 hover:bg-bg-surface transition-colors"
            : "indigo-card px-3 py-2.5 flex items-center justify-between gap-3 hover:bg-bg-raised active:bg-bg-raised transition-colors"
        }
      >
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-ok">
            {t("protocol.done_eyebrow", { defaultValue: "ВЫПОЛНЕНО · ДЕБРИФ" })}
          </div>
          <div className="text-sm text-fg-primary num-display mt-0.5">
            {dist != null && `${dist.toFixed(1)} ${t("unit_km", { defaultValue: "км" })}`}
            {dist != null && dur != null && " · "}
            {dur != null && `${Math.round(dur)}′`}
            {workout.rpe != null && ` · RPE ${workout.rpe}`}
          </div>
        </div>
        <span className="text-[11px] text-fg-muted shrink-0">
          {t("protocol.open_debrief", { defaultValue: "Разбор →" })}
        </span>
      </Link>
    );
  }

  // ── Фаза «до тренировки» — fueling + обувь ──
  const durationMin = workout.planned_duration_minutes;
  const carb =
    durationMin != null && durationMin >= CARB_FUELING_MIN_DURATION_MIN
      ? computeCarbPlan({
          bodyWeightKg: bodyWeightKg ?? 70,
          durationMin,
          intensity: "moderate",
        })
      : null;
  const pre30 = carb?.windows.find((w) => w.key === "pre_30m");
  const during = carb?.windows.find((w) => w.key === "during");

  const shoeItems = Array.isArray(shoes) ? shoes : shoes?.items ?? [];
  const activeShoe = isRunLike
    ? shoeItems.find((s) => !s.retired_at && s.is_primary) ??
      shoeItems.find((s) => !s.retired_at) ??
      null
    : null;

  if (!carb && !activeShoe && !win && !workout.target_zone && !workout.target_hr_max) return null;

  return (
    <div
      data-testid="today-protocol-row"
      className={
        embedded
          ? "mt-3 rounded-md bg-bg-raised px-3 py-2.5 flex gap-4 overflow-x-auto"
          : "indigo-card px-3 py-2.5 flex gap-4"
      }
    >
      {(workout.target_zone || workout.target_hr_max) && (
        <div className="flex-1 min-w-0" data-testid="protocol-execution">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-fg-muted">
            {t("protocol.execution", { defaultValue: "исполнение" })}
          </div>
          <div className="text-[11px] text-fg-secondary mt-0.5">
            {workout.target_zone}
            {workout.target_zone && workout.target_hr_max && " · "}
            {workout.target_hr_max &&
              t("protocol.hr_cap", { defaultValue: "HR cap {{bpm}}", bpm: workout.target_hr_max })}
          </div>
        </div>
      )}
      {carb && (
        <Link to="/carb-timing" className="flex-1 min-w-0" data-testid="protocol-fuel">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-fg-muted">
            {t("protocol.fuel", { defaultValue: "питание" })}
          </div>
          <div className="text-[11px] text-fg-secondary mt-0.5">
            {pre30 && `T−0:30 · ${pre30.carbGrams}${t("common:unit_g", { defaultValue: "г" })}`}
            {pre30 && during && " · "}
            {during && t("protocol.during", { defaultValue: "во время {{g}}г", g: during.carbGrams })}
          </div>
        </Link>
      )}
      {win && (
        <div className="flex-1 min-w-0" data-testid="protocol-weather-window">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-fg-muted">
            {t("protocol.weather_window", { defaultValue: "окно погоды" })}
          </div>
          <div className="text-[11px] text-fg-secondary mt-0.5">
            {String(win.start_hour).padStart(2, "0")}:00–{String(win.end_hour).padStart(2, "0")}:00
            {" · "}{Math.round(win.avg_temp_c)}°
            {win.max_precip_prob >= PRECIP_WARN_PROB &&
              ` · ${t("protocol.rain_risk", { defaultValue: "дождь {{p}}%", p: Math.round(win.max_precip_prob) })}`}
          </div>
        </div>
      )}
      {activeShoe && (
        <Link to="/shoes" className="flex-1 min-w-0" data-testid="protocol-shoe">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-fg-muted">
            {t("protocol.shoe", { defaultValue: "обувь" })}
          </div>
          <div className="text-[11px] text-fg-secondary mt-0.5 truncate">
            {activeShoe.nickname || activeShoe.shoe_model?.name || "—"} ·{" "}
            {Math.round(activeShoe.current_km)} {t("unit_km", { defaultValue: "км" })}
          </div>
        </Link>
      )}
    </div>
  );
}
